import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { X, Search } from "lucide-react";

/**
 * Multi-select of territory units from the master (#territory-master).
 *
 * Selected units render as removable chips; below them, a searchable
 * checkbox list grouped Patna zones first (four rows that would otherwise
 * drown between Nawada and Purnea in an alphabetical list of 41). Archived
 * units are excluded — they exist for history, not assignment.
 *
 * Controlled: `value` is the array of unit names, `onChange` gets the next
 * array. The component fetches the master itself on mount so both dialogs
 * (user edit, invite) stay a one-line integration.
 */
export default function TerritoryPicker({ value = [], onChange, testIdPrefix = "territory-picker", fieldRole = false }) {
  const [units, setUnits] = useState(null); // null = loading
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/territories")
      .then(({ data }) =>
        !cancelled &&
        // For a FIELD-role target the non-assignable units (HQ/Office, D10)
        // are dropped — the backend refuses that pairing at the validator, so
        // the picker must not offer it. `assignable` is the backend's boolean;
        // the caller only says who the target is.
        setUnits((data.items || []).filter(
          (u) => !u.archived && (!fieldRole || u.assignable !== false))))
      .catch((e) => {
        if (!cancelled) {
          setUnits([]);
          setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      });
    return () => { cancelled = true; };
    // Refetch when the target's field-ness changes (a role change in the
    // parent dialog re-filters which units may be offered).
  }, [fieldRole]);

  // `value` is a list of unit IDS since the id migration. Names are what the
  // user sees; ids are what travels. A name can be renamed out from under an
  // open dialog, an id cannot — so a picker rendered against a slightly stale
  // unit list still submits something the server resolves.
  const selected = useMemo(() => new Set(value), [value]);
  const nameById = useMemo(
    () => Object.fromEntries((units || []).map((u) => [u.id, u.name])),
    [units]
  );

  const shown = useMemo(() => {
    if (!units) return [];
    const s = search.trim().toLowerCase();
    const match = s ? units.filter((u) => u.name.toLowerCase().includes(s)) : units;
    return [
      { label: "Patna zones", items: match.filter((u) => u.kind === "patna_zone") },
      { label: "Districts", items: match.filter((u) => u.kind !== "patna_zone") },
    ].filter((g) => g.items.length > 0);
  }, [units, search]);

  function toggle(id) {
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <div className="mt-1.5 rounded-md border border-callisto" data-testid={testIdPrefix}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 border-b border-callisto" data-testid={`${testIdPrefix}-chips`}>
          {value.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full bg-callisto-navy text-white text-[11px] font-semibold pl-2.5 pr-1 py-0.5"
              data-testid={`${testIdPrefix}-chip-${nameById[id] || id}`}
            >
              {/* Falls back to the id only while the list is still loading —
                  never silently blank, so a missing name is visible. */}
              {nameById[id] || "…"}
              <button
                type="button"
                onClick={() => toggle(id)}
                aria-label={`Remove ${nameById[id] || id}`}
                className="rounded-full p-0.5 hover:bg-white/20"
                data-testid={`${testIdPrefix}-remove-${nameById[id] || id}`}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative border-b border-callisto">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-callisto-muted" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search units…"
          className="h-9 pl-8 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
          data-testid={`${testIdPrefix}-search`}
        />
      </div>

      <div className="max-h-44 overflow-y-auto p-1.5" data-testid={`${testIdPrefix}-list`}>
        {units === null ? (
          <p className="text-xs text-callisto-muted px-2 py-1.5">Loading units…</p>
        ) : error ? (
          <p className="text-xs text-red-700 px-2 py-1.5">{error}</p>
        ) : shown.length === 0 ? (
          <p className="text-xs text-callisto-muted px-2 py-1.5">No units match.</p>
        ) : (
          shown.map((group) => (
            <div key={group.label}>
              <p className="px-2 pt-1.5 pb-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-callisto-gold">
                {group.label}
              </p>
              {group.items.map((u) => (
                <label
                  key={u.id || u.name}
                  className="flex items-center gap-2 px-2 py-1 rounded hover:bg-callisto-app cursor-pointer text-sm text-callisto-navy"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggle(u.id)}
                    className="h-3.5 w-3.5 rounded border-callisto accent-callisto-navy"
                    data-testid={`${testIdPrefix}-option-${u.name}`}
                  />
                  {u.name}
                </label>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
